import { Button } from '../ui/button';
import {
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Magnet,
  Undo2,
  Redo2,
} from 'lucide-react';

/**
 * Top toolbar above the PDF canvas in the template editor — page nav,
 * zoom controls, fit-to-width, snap toggle, undo/redo.
 *
 * Pure presentational: state lives in TemplateEditorPage, this only
 * fires callbacks. Kept as a single component (not split per group)
 * because the visual grouping matters and the props payload is small.
 */
export const PdfEditorToolbar = ({
  currentPage,
  totalPages,
  onPageChange,
  scale,
  onScaleChange,
  onFitToWidth,
  snapEnabled,
  onToggleSnap,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  minScale = 0.5,
  maxScale = 1.5,
}) => (
  <div className="flex items-center justify-between mb-3 pb-3 border-b border-border">
    <div className="flex items-center gap-2">
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(Math.max(1, currentPage - 1))}
        disabled={currentPage <= 1}
      >
        <ChevronLeft className="w-4 h-4" />
      </Button>
      <span className="text-sm font-medium px-2">
        Page {currentPage} of {totalPages || 1}
      </span>
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(Math.min(totalPages || 1, currentPage + 1))}
        disabled={currentPage >= (totalPages || 1)}
      >
        <ChevronRight className="w-4 h-4" />
      </Button>
    </div>
    <div className="flex items-center gap-2">
      <Button variant="outline" size="sm" onClick={() => onScaleChange(Math.max(minScale, scale - 0.1))}>
        <ZoomOut className="w-4 h-4" />
      </Button>
      <span className="text-sm font-medium w-16 text-center">
        {Math.round(scale * 100)}%
      </span>
      <Button variant="outline" size="sm" onClick={() => onScaleChange(Math.min(maxScale, scale + 0.1))}>
        <ZoomIn className="w-4 h-4" />
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={onFitToWidth}
        title="Fit document width to panel"
        data-testid="fit-to-width-btn"
        className="px-2 text-xs"
      >
        Fit
      </Button>

      <div className="border-l pl-3 ml-2 flex items-center gap-2">
        <Button
          variant={snapEnabled ? 'default' : 'outline'}
          size="sm"
          onClick={onToggleSnap}
          className={snapEnabled ? 'bg-primary' : ''}
          title={snapEnabled ? 'Snap enabled - Press to disable' : 'Snap disabled - Press to enable'}
        >
          <Magnet className="w-4 h-4 mr-1" />
          Snap
        </Button>
      </div>

      <div className="border-l pl-3 ml-2 flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          onClick={onUndo}
          disabled={!canUndo}
          title="Undo (Ctrl+Z)"
          className="px-2"
        >
          <Undo2 className="w-4 h-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onRedo}
          disabled={!canRedo}
          title="Redo (Ctrl+Shift+Z)"
          className="px-2"
        >
          <Redo2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  </div>
);
